import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { projectsApi, teamsApi, documentsApi } from '../services/api';
import { isPrimaryDocLayout } from '../lib/projectLayouts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  ChevronDown, Heart, LayoutList, LayoutGrid, Calendar,
  HelpCircle, Users, Table2, GanttChart, LayoutTemplate, History,
  Lock, Eye, MessageCirclePlus,
  FileText, Presentation, Network, Globe, Folder, Database,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalClose,
  ResponsiveModalBody,
  ResponsiveModalFooter,
} from './ui/responsive-modal';

import { APP_COLORS as PROJECT_COLORS } from '../constants/colors';

// Project layout options grouped by content family. `layout` describes the
// project's primary default content - either a task view (with sections,
// view tabs etc.) or a document type that becomes the project's anchor.
const LAYOUT_GROUPS = [
  {
    groupKey: 'tasks',
    titleTKey: 'projects.layout_group_tasks',
    options: [
      { id: 'table',    tKey: 'projects.layout_table',    icon: Table2 },
      { id: 'list',     tKey: 'projects.layout_list',     icon: LayoutList },
      { id: 'board',    tKey: 'projects.layout_board',    icon: LayoutGrid },
      { id: 'calendar', tKey: 'projects.layout_calendar', icon: Calendar },
      { id: 'timeline', tKey: 'projects.layout_timeline', icon: GanttChart },
      { id: 'gallery',  tKey: 'projects.layout_gallery',  icon: LayoutTemplate },
    ],
  },
  {
    groupKey: 'documents',
    titleTKey: 'projects.layout_group_documents',
    options: [
      { id: 'document',    tKey: 'projects.layout_document',    icon: FileText },
      { id: 'folder',      tKey: 'projects.layout_folder',      icon: Folder },
      { id: 'database',    tKey: 'projects.layout_database',    icon: Database },
      { id: 'whiteboard',  tKey: 'projects.layout_whiteboard',  icon: Presentation },
      { id: 'mindmap',     tKey: 'projects.layout_mindmap',     icon: Network },
      { id: 'embed',       tKey: 'projects.layout_embed',       icon: Globe },
      { id: 'changelog',   tKey: 'projects.layout_changelog',   icon: History },
    ],
  },
];

const VISIBILITY_OPTIONS = [
  { id: 'private',    icon: Lock,                tKey: 'projects.public_visibility.private',    descKey: 'projects.public_visibility.private_desc' },
  { id: 'view',       icon: Eye,                 tKey: 'projects.public_visibility.view',       descKey: 'projects.public_visibility.view_desc' },
  { id: 'contribute', icon: MessageCirclePlus,   tKey: 'projects.public_visibility.contribute', descKey: 'projects.public_visibility.contribute_desc' },
];

const AddProjectModal = ({ isOpen, onClose, onProjectCreated, editingProject = null, defaultTeamId = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    color: '#6B7280',
    team_id: null,
    parent_id: null,
    is_favorite: false,
    layout: 'table',
    description: '',
    public_visibility: 'private',
  });
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Dropdown states
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showWorkspacePicker, setShowWorkspacePicker] = useState(false);
  const [showParentPicker, setShowParentPicker] = useState(false);

  // Check if we're actually editing (has id) or just creating with defaults
  const isEditing = editingProject && editingProject.id;

  useEffect(() => {
    if (isOpen) {
      // Clear any stale error left over from a previous open (e.g. a failed
      // save) so the user doesn't see an outdated message on a fresh form.
      setError(null);
      fetchData();
      if (isEditing) {
        // Editing existing project
        setFormData({
          name: editingProject.name || '',
          color: editingProject.color || '#6B7280',
          team_id: editingProject.team_id || null,
          parent_id: editingProject.parent_id || null,
          is_favorite: editingProject.is_favorite || false,
          layout: editingProject.layout || 'table',
          description: editingProject.description || '',
          public_visibility: editingProject.public_visibility || 'private',
        });
      } else {
        // Creating new project
        setFormData({
          name: '',
          color: '#6B7280',
          team_id: defaultTeamId || null,
          parent_id: null,
          is_favorite: false,
          layout: 'table',
          description: '',
          public_visibility: 'private',
        });
      }
    }
  }, [isOpen, editingProject, defaultTeamId, isEditing]);

  const fetchData = async () => {
    try {
      const [teamsRes, projectsRes] = await Promise.all([
        teamsApi.getAll().catch(() => ({ data: [] })),
        projectsApi.getAll().catch(() => ({ data: [] }))
      ]);
      setTeams(teamsRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(t('projects.project_name_required'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditing) {
        // BG_110: suppress the global upgrade dialog - a 402 is surfaced via
        // the inline error below. Letting the global prompt fire too left it
        // hidden behind this modal, only appearing after the user hit Cancel.
        await projectsApi.update(editingProject.id, formData, { _suppressUpgradePrompt: true });
        onProjectCreated();
      } else {
        const projectRes = await projectsApi.create(formData, { _suppressUpgradePrompt: true });
        const newProject = projectRes?.data;

        // Document-layout projects (whiteboard / spreadsheet / mindmap / embed
        // / plain document) auto-create a single primary document of that type
        // and drop the user straight into its editor. Changelog is excluded -
        // its project is a feed and starts empty by design.
        if (newProject?.id && isPrimaryDocLayout(formData.layout)) {
          try {
            const docRes = await documentsApi.create({
              type: formData.layout,
              title: formData.name,
              project_id: newProject.id,
              team_id: formData.team_id || null,
            });
            const newDocId = docRes?.data?.id;
            onProjectCreated();
            if (newDocId) {
              navigate(`/app/documents/${newDocId}`);
              return;
            }
          } catch (docErr) {
            // 402 → plan gate already raises an upgrade prompt via the api
            // interceptor. The empty project is left in place so the user can
            // change its layout or upgrade and retry. Show a short notice for
            // any other failure.
            if (docErr.response?.status !== 402) {
              toast.error(
                t('projects.primary_doc_create_failed', {
                  defaultValue: 'Project created, but its primary document could not be added.',
                }),
              );
            }
            onProjectCreated();
          }
        } else {
          onProjectCreated();
        }
      }
    } catch (err) {
      console.error('Error saving project:', err);
      const detail = err.response?.data?.detail;
      const detailMsg = typeof detail === 'object' ? detail?.message : detail;
      if (err.response?.status === 402) {
        // BG_66: For a plan-gated layout the api interceptor fires the global
        // upgrade prompt, but it renders *behind* this Radix dialog (same
        // z-index, and the dialog portal sits later in the DOM while Radix
        // blocks outside interaction), so the user saw nothing on Save and
        // only discovered the upgrade dialog after closing the modal. Surface
        // a localized inline message so the failure reason is visible at once.
        //
        // BG_75: a 402 here can mean two different things - the chosen layout
        // is premium (feature_not_available) or the plan's project quota is
        // full (projects_limit_reached). Distinguish on detail.error so the
        // message matches the real cause instead of always blaming the layout.
        const errCode = detail?.error;
        if (errCode === 'projects_limit_reached') {
          setError(detailMsg || t('projects.project_limit_reached', {
            defaultValue: 'Your plan\'s project limit has been reached. Upgrade to add more projects.',
          }));
        } else {
          setError(t('projects.layout_requires_upgrade', {
            layout: t(`projects.layout_${formData.layout}`, { defaultValue: formData.layout }),
            defaultValue: 'The {{layout}} view requires a higher plan. Upgrade to unlock it.',
          }));
        }
      } else {
        setError(detailMsg || t('projects.failed_to_save_project'));
      }
    } finally {
      setSaving(false);
    }
  };

  const getSelectedColorName = () => {
    const color = PROJECT_COLORS.find(c => c.value === formData.color);
    return color?.name || t('ui_common.colors.charcoal');
  };

  const getSelectedWorkspace = () => {
    if (!formData.team_id) return t('projects.my_projects');
    const team = teams.find(tm => tm.id === formData.team_id);
    return team?.name || t('projects.my_projects');
  };

  const getSelectedParent = () => {
    if (!formData.parent_id) return t('projects.no_parent');
    const project = projects.find(p => p.id === formData.parent_id);
    return project?.name || t('projects.no_parent');
  };

  return (
    <ResponsiveModal open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <ResponsiveModalContent
        className="bg-popover border-border md:rounded-2xl"
        overlayClassName="bg-black/50"
      >
        <ResponsiveModalHeader className="px-6 border-border">
          <ResponsiveModalTitle className="text-lg text-foreground flex items-center gap-2">
            {isEditing ? t('projects.edit_project') : t('projects.add_project')}
            <button className="p-1 text-muted-foreground hover:text-muted-foreground">
              <HelpCircle className="w-4 h-4" />
            </button>
          </ResponsiveModalTitle>
          <ResponsiveModalClose className="text-muted-foreground hover:bg-accent hover:text-foreground p-2" />
        </ResponsiveModalHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <ResponsiveModalBody className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="space-y-2">
            <Label className="text-foreground/80">{t('common.name')}</Label>
            <div className="relative">
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder=""
                maxLength={120}
                className="bg-accent/60 border-border text-foreground pr-16"
                autoFocus
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {formData.name.length}/120
              </span>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label className="text-foreground/80">{t('common.color')}</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="w-full flex items-center justify-between px-3 py-2 bg-accent/60 border border-border rounded-lg text-foreground hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: formData.color }}
                  />
                  <span>{getSelectedColorName()}</span>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>

              {showColorPicker && (
                <div className="absolute z-10 mt-1 w-full bg-accent border border-border rounded-lg shadow-xl p-3 max-h-60 overflow-y-auto">
                  <div className="grid grid-cols-6 gap-2">
                    {PROJECT_COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, color: color.value });
                          setShowColorPicker(false);
                        }}
                        className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                          formData.color === color.value ? 'ring-2 ring-white ring-offset-2 ring-offset-[#252527]' : ''
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Workspace */}
          <div className="space-y-2">
            <Label className="text-foreground/80">{t('projects.workspace')}</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowWorkspacePicker(!showWorkspacePicker)}
                className="w-full flex items-center justify-between px-3 py-2 bg-accent/60 border border-border rounded-lg text-foreground hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-2">
                  {formData.team_id ? (
                    <Users className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Folder className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                  )}
                  <span>{getSelectedWorkspace()}</span>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>

              {showWorkspacePicker && (
                <div className="absolute z-10 mt-1 w-full bg-accent border border-border rounded-lg shadow-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, team_id: null });
                      setShowWorkspacePicker(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent ${
                      !formData.team_id ? 'bg-accent' : ''
                    }`}
                  >
                    <Folder className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                    <span className="text-foreground">{t('projects.my_projects')}</span>
                  </button>
                  {teams.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, team_id: team.id });
                        setShowWorkspacePicker(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent ${
                        formData.team_id === team.id ? 'bg-accent' : ''
                      }`}
                    >
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="text-foreground">{team.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Parent Project */}
          <div className="space-y-2">
            <Label className="text-foreground/80">{t('projects.parent_project')}</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowParentPicker(!showParentPicker)}
                className="w-full flex items-center justify-between px-3 py-2 bg-accent/60 border border-border rounded-lg text-foreground hover:bg-accent transition-colors"
              >
                <span>{getSelectedParent()}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>

              {showParentPicker && (
                <div className="absolute z-10 mt-1 w-full bg-accent border border-border rounded-lg shadow-xl overflow-hidden max-h-48 overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, parent_id: null });
                      setShowParentPicker(false);
                    }}
                    className={`w-full px-3 py-2 text-left hover:bg-accent ${
                      !formData.parent_id ? 'bg-accent' : ''
                    }`}
                  >
                    <span className="text-foreground">{t('projects.no_parent')}</span>
                  </button>
                  {projects
                    .filter(p => p.id !== editingProject?.id && p.parent_id !== editingProject?.id)
                    .map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, parent_id: project.id });
                          setShowParentPicker(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent ${
                          formData.parent_id === project.id ? 'bg-accent' : ''
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: project.color || '#6B7280' }}
                        />
                        <span className="text-foreground truncate">{project.name}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Add to favorites */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <Heart className={`w-4 h-4 ${formData.is_favorite ? 'text-red-400 fill-red-400' : 'text-muted-foreground'}`} />
              <span className="text-foreground">{t('projects.add_to_favorites')}</span>
            </div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, is_favorite: !formData.is_favorite })}
              className={`w-10 h-6 rounded-full transition-colors ${
                formData.is_favorite ? 'bg-amber-500' : 'bg-accent'
              }`}
            >
              <div
                className={`w-4 h-4 bg-white rounded-full transition-transform ${
                  formData.is_favorite ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Layout - split into Tasks vs Documents groups so the user picks
              the project's primary content family explicitly. */}
          <div className="space-y-2">
            <Label className="text-foreground/80">{t('projects.layout')}</Label>
            <div className="space-y-3">
              {LAYOUT_GROUPS.map(group => (
                <div key={group.groupKey}>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1.5 px-1">
                    {t(group.titleTKey, { defaultValue: group.groupKey === 'tasks' ? 'Tasks' : 'Documents' })}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {group.options.map(layout => {
                      const Icon = layout.icon;
                      const selected = formData.layout === layout.id;
                      return (
                        <button
                          key={layout.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, layout: layout.id })}
                          className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-colors ${
                            selected
                              ? 'bg-amber-500/15 border-amber-500/50 text-amber-700 dark:text-amber-400'
                              : 'bg-accent/60 border-transparent text-muted-foreground hover:text-foreground hover:bg-accent hover:border-border'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="text-xs">{t(layout.tKey)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Public visibility - only relevant if the chosen team is publicly shared */}
          {(() => {
            const selectedTeam = teams.find(tm => tm.id === formData.team_id);
            if (!selectedTeam?.is_public) return null;
            return (
              <div className="space-y-2">
                <Label className="text-foreground/80">
                  {t('projects.public_visibility_label', { defaultValue: 'Public visibility' })}
                </Label>
                <div className="space-y-1.5">
                  {VISIBILITY_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const selected = formData.public_visibility === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, public_visibility: opt.id })}
                        className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors border ${
                          selected
                            ? 'bg-amber-500/10 border-amber-500/40'
                            : 'bg-accent/60 border-border hover:border-border'
                        }`}
                      >
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${selected ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${selected ? 'text-foreground' : 'text-foreground'}`}>
                            {t(opt.tKey, { defaultValue: opt.id })}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {t(opt.descKey, {
                              defaultValue:
                                opt.id === 'private' ? 'Hidden from the public team page.' :
                                opt.id === 'view' ? 'Anyone with the public link can read this project.' :
                                'Visible publicly. Signed-in BusyBee.Day users can submit and upvote tasks.',
                            })}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          </ResponsiveModalBody>

          <ResponsiveModalFooter className="px-6 py-4 gap-3 border-border bg-popover">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-border text-foreground/80"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving || !formData.name.trim()}
              className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-50"
            >
              {saving ? t('common.saving') : isEditing ? t('common.save') : t('common.add')}
            </Button>
          </ResponsiveModalFooter>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};

export default AddProjectModal;
